/**
 * Address arithmetic for the network allowlist screen.
 *
 * The screen has to answer two questions about the address the server reports:
 * is this device already covered by something on the list, and what should be
 * written down if it is not. Both need real prefix matching — a device on a
 * dual-stack office line usually arrives over IPv6, whose host bits Airtel
 * rotates every few hours, so only the /64 is stable enough to allow.
 */

const V6_BITS = 128;
const V4_BITS = 32;

function isV6(address) {
  return address.includes(':');
}

function expandV6(address) {
  const halves = address.split('::');
  if (halves.length > 2) return null;
  const head = halves[0] ? halves[0].split(':') : [];
  const tail = halves.length === 2 && halves[1] ? halves[1].split(':') : [];
  const fill = 8 - head.length - tail.length;
  if (halves.length === 1 ? fill !== 0 : fill < 1) return null;
  const groups = [...head, ...Array(fill).fill('0'), ...tail];
  if (!groups.every((g) => /^[0-9a-f]{1,4}$/i.test(g))) return null;
  return groups.map((g) => g.toLowerCase().padStart(4, '0'));
}

function toBigInt(address) {
  if (typeof address !== 'string' || !address) return null;
  if (isV6(address)) {
    const groups = expandV6(address);
    if (!groups) return null;
    return groups.reduce((acc, g) => (acc << 16n) + BigInt(parseInt(g, 16)), 0n);
  }
  const octets = address.split('.');
  if (octets.length !== 4) return null;
  if (!octets.every((o) => /^\d{1,3}$/.test(o) && Number(o) <= 255)) return null;
  return octets.reduce((acc, o) => (acc << 8n) + BigInt(o), 0n);
}

function maskFor(bits, length) {
  return ((1n << BigInt(length)) - 1n) << BigInt(bits - length);
}

/** How this device's network should be written down, or '' if it is not an address. */
export function suggestedCidr(address) {
  if (typeof address !== 'string' || toBigInt(address) === null) return '';
  if (!isV6(address)) return `${address}/32`;
  return `${expandV6(address).slice(0, 4).join(':')}::/64`;
}

/** Is this address inside any of the allowlist entries? */
export function addressCovered(address, entries) {
  const value = toBigInt(address);
  if (value === null) return false;
  const bits = isV6(address) ? V6_BITS : V4_BITS;

  return (entries || []).some((entry) => {
    const [base, prefix] = String(entry?.cidr || '').split('/');
    if (isV6(base) !== isV6(address)) return false;
    const network = toBigInt(base);
    if (network === null) return false;
    const length = prefix === undefined ? bits : Number(prefix);
    if (!Number.isInteger(length) || length < 0 || length > bits) return false;
    const mask = maskFor(bits, length);
    return (value & mask) === (network & mask);
  });
}
